import { z } from 'zod';
import { expectedRevisionSchema, idSchema } from '@nw/contracts';

export { libraryManifest } from '../manifest.js';

export const BOOK_STATUSES = ['want_to_read', 'reading', 'paused', 'finished'] as const;
export type BookStatus = (typeof BOOK_STATUSES)[number];
export const STATUS_LABELS: Record<BookStatus, string> = {
  want_to_read: 'Want to read',
  reading: 'Reading',
  paused: 'Paused',
  finished: 'Finished',
};

const conceptKey = z.string().regex(/^mind-networks:concept:[a-z0-9_-]+$/, 'Not a concept reference');
const tag = z.string().trim().min(1).max(40);
const optionalText = (max: number) => z.string().trim().max(max).nullable().optional();
const isbn = z
  .string()
  .trim()
  .transform((v) => v.replace(/[-\s]/g, '').toUpperCase())
  .refine((v) => /^(\d{9}[\dX]|\d{13})$/.test(v), 'ISBN must have 10 or 13 digits');

export const authorInputSchema = z.union([
  z.object({ personId: idSchema }),
  z.object({ name: z.string().trim().min(1).max(160), providerId: z.string().regex(/^OL\d+A$/).nullable().optional() }),
]);
export type AuthorInput = z.infer<typeof authorInputSchema>;

export const metadataSourceSchema = z.object({
  provider: z.literal('openlibrary'),
  workId: z.string().regex(/^OL\d+W$/),
  editionId: z.string().regex(/^OL\d+M$/).nullable().optional(),
});

export const coverSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('none') }),
  z.object({ kind: z.literal('openlibrary'), coverId: z.number().int().positive() }),
]);

const bookFields = {
  title: z.string().trim().min(1, 'A title is required').max(300),
  subtitle: optionalText(300),
  authors: z.array(authorInputSchema).max(12),
  isbn: isbn.nullable().optional(),
  pageCount: z.number().int().min(1).max(20000).nullable().optional(),
  status: z.enum(BOOK_STATUSES),
  tags: z.array(tag).max(12),
  publishedYear: z.number().int().min(-3000).max(2100).nullable().optional(),
  publisher: optionalText(200),
  format: optionalText(40),
  description: optionalText(4000),
  cover: coverSchema.optional(),
  topicKeys: z.array(conceptKey).max(24),
};

export const createBookSchema = z.object({
  ...bookFields,
  authors: bookFields.authors.default([]),
  tags: bookFields.tags.default([]),
  topicKeys: bookFields.topicKeys.default([]),
  status: bookFields.status.default('want_to_read'),
  shelfIds: z.array(idSchema).max(20).default([]),
  metadata: metadataSourceSchema.nullable().optional(),
});
export type CreateBookInput = z.input<typeof createBookSchema>;

export const updateBookSchema = z.object({
  title: bookFields.title.optional(),
  subtitle: bookFields.subtitle,
  authors: bookFields.authors.optional(),
  isbn: bookFields.isbn,
  pageCount: bookFields.pageCount,
  status: bookFields.status.optional(),
  tags: bookFields.tags.optional(),
  publishedYear: bookFields.publishedYear,
  publisher: bookFields.publisher,
  format: bookFields.format,
  description: bookFields.description,
  cover: bookFields.cover,
  topicKeys: bookFields.topicKeys.optional(),
  pdfAssetId: idSchema.nullable().optional(),
  metadata: metadataSourceSchema.nullable().optional(),
  /** Enrichment from a provider: fields the person edited are kept unless listed in `overwrite`. */
  source: z.enum(['user', 'enrichment']).default('user'),
  overwrite: z.array(z.string()).max(20).default([]),
  expectedRevision: expectedRevisionSchema,
});
export type UpdateBookInput = z.input<typeof updateBookSchema>;

export const bookSchema = z.object({
  id: z.string(),
  title: z.string(),
  subtitle: z.string().nullable(),
  authors: z.array(z.object({ personId: z.string(), name: z.string() })),
  isbn: z.string().nullable(),
  pageCount: z.number().nullable(),
  status: z.enum(BOOK_STATUSES),
  tags: z.array(z.string()),
  publishedYear: z.number().nullable(),
  publisher: z.string().nullable(),
  format: z.string().nullable(),
  description: z.string().nullable(),
  cover: z.object({ kind: z.enum(['none', 'openlibrary']), url: z.string().nullable() }),
  spine: z.object({ color: z.string(), ink: z.string(), heightRatio: z.number(), widthRatio: z.number() }),
  topicKeys: z.array(z.string()),
  pdf: z.object({ assetId: z.string(), bytes: z.number(), originalName: z.string() }).nullable(),
  metadata: z.object({ provider: z.string(), workId: z.string(), editionId: z.string().nullable(), fetchedAt: z.string() }).nullable(),
  userEditedFields: z.array(z.string()),
  shelfIds: z.array(z.string()),
  reading: z
    .object({
      currentPage: z.number().nullable(),
      pdfPageIndex: z.number().nullable(),
      lastReadAt: z.string().nullable(),
      revision: z.number(),
    })
    .nullable(),
  isDemo: z.boolean(),
  deletedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  revision: z.number(),
});
export type Book = z.infer<typeof bookSchema>;

export const listBooksQuerySchema = z.object({
  q: z.string().trim().max(120).optional(),
  status: z.enum(BOOK_STATUSES).optional(),
  shelf: idSchema.optional(),
  tag: tag.optional(),
  author: idSchema.optional(),
  sort: z.enum(['recent', 'title']).default('recent'),
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(60),
  deleted: z.enum(['true', 'false']).default('false'),
});
export type ListBooksQuery = z.input<typeof listBooksQuerySchema>;

export const libraryStatsSchema = z.object({
  total: z.number(),
  byStatus: z.record(z.string(), z.number()),
  tags: z.array(z.object({ tag: z.string(), count: z.number() })),
});
export type LibraryStats = z.infer<typeof libraryStatsSchema>;

export const recordReadingSchema = z
  .object({
    startedAt: z.iso.datetime({ offset: true }),
    endedAt: z.iso.datetime({ offset: true }).nullable().optional(),
    durationMinutes: z.number().int().min(0).max(24 * 60).nullable().optional(),
    fromPage: z.number().int().min(0).max(20000).nullable().optional(),
    toPage: z.number().int().min(0).max(20000).nullable().optional(),
    pdfPageIndex: z.number().int().min(0).max(20000).nullable().optional(),
    note: z.string().trim().max(2000).nullable().optional(),
    status: z.enum(BOOK_STATUSES).optional(),
    expectedRevision: expectedRevisionSchema,
  })
  .refine((v) => v.fromPage == null || v.toPage == null || v.toPage >= v.fromPage, {
    message: 'The end page must be after the start page',
    path: ['toPage'],
  });
export type RecordReadingInput = z.input<typeof recordReadingSchema>;

/** Position/correction updates. Corrections may lower progress; history is never rewritten. */
export const updatePositionSchema = z.object({
  currentPage: z.number().int().min(0).max(20000).nullable().optional(),
  pdfPageIndex: z.number().int().min(0).max(20000).nullable().optional(),
  expectedRevision: expectedRevisionSchema,
});

export const readingSessionSchema = z.object({
  id: z.string(),
  bookId: z.string(),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
  durationSeconds: z.number().nullable(),
  fromPage: z.number().nullable(),
  toPage: z.number().nullable(),
  pdfPageIndex: z.number().nullable(),
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type ReadingSession = z.infer<typeof readingSessionSchema>;

export const noteInputSchema = z.object({
  body: z.string().trim().min(1, 'Write something first').max(20000),
  page: z.number().int().min(0).max(20000).nullable().optional(),
  pdfPageIndex: z.number().int().min(0).max(20000).nullable().optional(),
  conceptKeys: z.array(conceptKey).max(24).default([]),
});
export const updateNoteSchema = noteInputSchema.partial().extend({ expectedRevision: expectedRevisionSchema });

export const noteSchema = z.object({
  id: z.string(),
  bookId: z.string(),
  bookTitle: z.string().nullable(),
  body: z.string(),
  page: z.number().nullable(),
  pdfPageIndex: z.number().nullable(),
  conceptKeys: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
  revision: z.number(),
});
export type BookNote = z.infer<typeof noteSchema>;

export const shelfInputSchema = z.object({ name: z.string().trim().min(1).max(60) });
export const shelfSchema = z.object({
  id: z.string(),
  name: z.string(),
  sortOrder: z.number(),
  bookCount: z.number(),
  revision: z.number(),
});
export type Shelf = z.infer<typeof shelfSchema>;

// ---- Public capabilities ---------------------------------------------------------------------

export const bookSummarySchema = z.object({
  key: z.string(),
  id: z.string(),
  title: z.string(),
  authorNames: z.array(z.string()),
  status: z.enum(BOOK_STATUSES),
  coverUrl: z.string().nullable(),
  href: z.string(),
});
export type BookSummary = z.infer<typeof bookSummarySchema>;

export const getSummaryInputSchema = z.object({ bookIds: z.array(idSchema).min(1).max(50) });
export const findByTopicsInputSchema = z.object({
  topicKeys: z.array(conceptKey).min(1).max(20),
  cursor: z.string().max(200).optional(),
  limit: z.number().int().min(1).max(50).default(10),
});

// ---- Home contributions ----------------------------------------------------------------------

export const resumeReadingSchema = z.object({
  book: bookSummarySchema.extend({ pageCount: z.number().nullable(), hasPdf: z.boolean(), spineColor: z.string() }),
  currentPage: z.number().nullable(),
  pdfPageIndex: z.number().nullable(),
  lastReadAt: z.string().nullable(),
});
export type ResumeReading = z.infer<typeof resumeReadingSchema>;

export const recentBookNotesSchema = z.array(
  z.object({ id: z.string(), excerpt: z.string(), bookId: z.string(), bookTitle: z.string(), page: z.number().nullable(), updatedAt: z.string() }),
);

/** Stable designed spine colours (never derived from cover thumbnails). */
export const SPINE_PALETTE = [
  { color: '#2F4858', ink: '#F4EFE6' },
  { color: '#E9E3D5', ink: '#2A2B2D' },
  { color: '#7A2E2E', ink: '#F6EBDD' },
  { color: '#C9B27C', ink: '#231F1A' },
  { color: '#3E5C4A', ink: '#EEF1E6' },
  { color: '#1F2A36', ink: '#E6E1D3' },
  { color: '#D8D2C4', ink: '#2F3437' },
  { color: '#8C5A3C', ink: '#F7EEE3' },
  { color: '#546A7B', ink: '#F2F4F3' },
  { color: '#A44A34', ink: '#FBF1E7' },
  { color: '#F2EDE3', ink: '#3A3A38' },
  { color: '#2D2D2D', ink: '#E9C46A' },
  { color: '#5F644F', ink: '#F4F1DE' },
  { color: '#A5A58D', ink: '#1D1E18' },
] as const;

export function spineFor(id: string, title: string, pageCount: number | null) {
  let h = 2166136261;
  for (const ch of id) h = Math.imul(h ^ ch.charCodeAt(0), 16777619) >>> 0;
  const swatch = SPINE_PALETTE[h % SPINE_PALETTE.length]!;
  const heightRatio = 0.78 + ((h >>> 8) % 22) / 100; // 0.78–0.99
  const pages = pageCount ?? 180 + ((h >>> 4) % 260);
  const widthRatio = Math.min(1.6, Math.max(0.7, 0.62 + pages / 520 + (title.length > 40 ? 0.1 : 0)));
  return { color: swatch.color, ink: swatch.ink, heightRatio, widthRatio: Math.round(widthRatio * 100) / 100 };
}
